import React from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { Card } from '../ui/card';
import { DeliveryMap, type LatLng } from '../shopper/DeliveryMap';
import { cn } from '../../lib/utils';

interface OrderDeliveryMapCardProps {
  store?: LatLng | null;
  dropoff?: LatLng | null;
  courier?: LatLng | null;
  isLoaded: boolean;
  geocoding?: boolean;
  hasAnyPoint: boolean;
  statusLabel?: string;
  legendLabels?: Partial<Record<'store' | 'dropoff' | 'courier', string>>;
  className?: string;
  mapClassName?: string;
}

export function OrderDeliveryMapCard({
  store,
  dropoff,
  courier,
  isLoaded,
  geocoding = false,
  hasAnyPoint,
  statusLabel,
  legendLabels,
  className,
  mapClassName = 'h-64',
}: OrderDeliveryMapCardProps) {
  const showMap = isLoaded && hasAnyPoint;

  return (
    <Card className={cn('overflow-hidden', className)}>
      <div className={cn('relative', mapClassName)}>
        {showMap ? (
          <>
            <DeliveryMap
              store={store}
              dropoff={dropoff}
              courier={courier}
              legendLabels={legendLabels}
              className="relative w-full h-full"
            />
            {statusLabel && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-1.5 shadow-sm flex items-center gap-2 pointer-events-none z-10">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium text-heading">{statusLabel}</span>
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200">
            <div className="text-center px-6">
              {geocoding || !isLoaded ? (
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
              ) : (
                <MapPin className="h-8 w-8 text-primary mx-auto mb-3" />
              )}
              <p className="font-medium text-heading text-sm">
                {!isLoaded ? 'Loading map…' : geocoding ? 'Locating addresses…' : 'Map unavailable'}
              </p>
              <p className="text-xs text-body mt-1">
                {!isLoaded
                  ? 'Google Maps is starting up'
                  : geocoding
                    ? 'Finding store and delivery locations'
                    : 'Could not resolve coordinates for this order'}
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
